import { Button, Input, Space, Form, InputNumber, Select, Modal, Table, Popconfirm } from 'antd'
import { LeftCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { PACKAGES } from '../../assets/constants/route_path'
import { formatCurrency } from '../../utils/string-utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
    apiAddMetaDataPackageDataSponsor,
    apiGetListMetaDataPackageDataSponsor,
    apiGetPackageDataSponsor,
    apiUpdatePackageDataSponsor,
    apiUpdateMetaDataPackageDataSponsor,
    apiDeleteMetaDataPackageDataSponsor
} from '../../api'
import { toastError, toastSuccess } from '../../utils/toast'
import { useLoading } from '../../context/LoadingContext'
import {
    groupCodeOptions,
    partnerPackageDataSponsorSourceOptions,
    providerSourceOptions,
    serviceCodeOptions
} from '../../assets/constants/default_option'
import {mapGroupCode, mapServiceCode, mapSourceProviders, serviceCode} from "../../assets/constants/constants";

interface ProviderMetadata {
    provider: string | null;
    serviceCode: string;
    price: number;
    discountAmount: number;
    netPrice: number;
    groupCode: string;
}

export default function PackageEditPackagesSponsor() {
    const [modalUpdate, setModalUpdate] = useState<{ data: any, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [otpConfirm, setOtpConfirm] = useState<string>('')
    const { setIsLoading } = useLoading()
    const navigate = useNavigate()
    const [formUpdate] = Form.useForm()
    const [searchParams] = useSearchParams()
    const id = searchParams.get('id')
    const navigateBack = PACKAGES + `?previousTab=dataSponsor`
    const [providerMetadata, setProviderMetadata] = useState<ProviderMetadata[]>([])
    const [initialProviderMetadata, setInitialProviderMetadata] = useState<ProviderMetadata[]>([])
    const [modalProviderEdit, setModalProviderEdit] = useState<{ isOpen: boolean, index: number | null }>({ isOpen: false, index: null })
    const [editingProvider, setEditingProvider] = useState<ProviderMetadata | null>(null)

    const handleAddProviderModal = () => {
        const priceUnit = formUpdate.getFieldValue('priceUnit') || 0
        setEditingProvider({
            provider: null,
            serviceCode: serviceCode.VDS_VIETTEL_KEY,
            price: priceUnit,
            discountAmount: 0,
            netPrice: priceUnit,
            groupCode: '3'
        })
        setModalProviderEdit({ isOpen: true, index: null })
    }

    const handleEditProviderModal = (index: number) => {
        setEditingProvider({ ...providerMetadata[index] })
        setModalProviderEdit({ isOpen: true, index })
    }

    const handleRemoveProvider = (index: number) => {
        const newProvider = providerMetadata.filter((_, i) => i !== index)
        setProviderMetadata(newProvider)
        toastSuccess('Đã xóa nhà cung cấp!')
    }

    const handleSaveProvider = () => {
        if (!editingProvider) return

        // Validate required fields
        if (!editingProvider.provider) {
            toastError('Vui lòng chọn nhà cung cấp!')
            return
        }
        if (!editingProvider.serviceCode || editingProvider.serviceCode.trim() === '') {
            toastError('Vui lòng chọn dịch vụ!')
            return
        }
        if (editingProvider.price === undefined || editingProvider.price === null || editingProvider.price <= 0) {
            toastError('Vui lòng nhập giá gốc hợp lệ!')
            return
        }
        if (editingProvider.discountAmount === undefined || editingProvider.discountAmount === null || editingProvider.discountAmount < 0) {
            toastError('Vui lòng nhập giá chiết khấu hợp lệ!')
            return
        }
        if (!editingProvider.groupCode || editingProvider.groupCode.trim() === '') {
            toastError('Vui lòng chọn nhóm!')
            return
        }

        if (modalProviderEdit.index !== null) {
            // Edit existing provider
            const newProviders = [...providerMetadata]
            newProviders[modalProviderEdit.index] = editingProvider
            setProviderMetadata(newProviders)
            toastSuccess('Đã cập nhật nhà cung cấp!')
        } else {
            // Add new provider
            if (providerMetadata.length >= providerSourceOptions.length) {
                toastError(`Số lượng nhà cung cấp tối đa là ${providerSourceOptions.length}!`)
                return
            }
            // Check for duplicate provider
            const isDuplicate = providerMetadata.some(p => p.provider === editingProvider.provider)
            if (isDuplicate) {
                toastError('Nhà cung cấp này đã tồn tại!')
                return
            }
            setProviderMetadata([...providerMetadata, editingProvider])
            toastSuccess('Đã thêm nhà cung cấp!')
        }

        setModalProviderEdit({ isOpen: false, index: null })
        setEditingProvider(null)
    }

    const handleProviderFieldChange = (field: keyof ProviderMetadata, value: any) => {
        if (!editingProvider) return

        const newProvider = { ...editingProvider, [field]: value }

        // Auto-calculate netPrice when discountAmount or price changes
        if (field === 'discountAmount' || field === 'price') {
            const price = field === 'price' ? value : editingProvider.price
            const discount = field === 'discountAmount' ? value : editingProvider.discountAmount
            newProvider.netPrice = (price || 0) - (discount || 0)
        }

        setEditingProvider(newProvider)
    }

    useEffect(() => {
        const fetchPackage = async () => {
            try {
                setIsLoading(true)
                const resp = await apiGetPackageDataSponsor({ id })
                if (resp.data !== null) {
                    const packageData = resp.data.package

                    // Set form fields
                    formUpdate.setFieldsValue(resp.data.package)

                    // Fetch and load metadata into providerMetadata state
                    const metaDataResp = await apiGetListMetaDataPackageDataSponsor({
                        source: packageData.source,
                        code: packageData.dataPack
                    })

                    if (metaDataResp?.data?.metadataPackages?.length) {
                        const loadedProviders = metaDataResp.data.metadataPackages.map((meta: any) => ({
                            provider: meta.provider || null,
                            serviceCode: meta.service_code,
                            price: meta.original_price || 0,
                            discountAmount: meta.discount_amount || 0,
                            netPrice: meta.net_price || 0,
                            groupCode: meta.group_code
                        }))
                        setProviderMetadata(loadedProviders)
                        setInitialProviderMetadata(loadedProviders) // Save initial state for comparison
                    } else {
                        setProviderMetadata([])
                        setInitialProviderMetadata([]) // No initial metadata
                    }
                }
            } catch (error) {
                console.log(error)
                toastError('Có lỗi xảy ra!')
            } finally {
                setIsLoading(false)
            }
        }
        if (id) {
            fetchPackage()
        }
    }, [id])

    const handleOpenModal = (values: any) => {
        setModalUpdate({ data: values, isOpen: true })
    }

    const handleCloseModal = () => {
        setModalUpdate({ data: null, isOpen: false })
        if (otpConfirm) {
            setOtpConfirm('')
        }
    }

    const handleConfirm = async () => {
        if (!otpConfirm) {
            toastError('Vui lòng nhập mã OTP xác nhận!')
            return
        }

        try {
            setIsLoading(true)
            // Update package first
            const resp = await apiUpdatePackageDataSponsor({ otpConfirm, package: modalUpdate.data })

            if (resp.status !== 200) {
                toastError('Cập nhật gói cước thất bại!')
                return
            }

            // Get valid current providers
            const validProviders = providerMetadata.filter(p => p.provider)

            // Create sets for easier comparison
            const currentProviderSet = new Set(validProviders.map(p => p.provider))
            const initialProviderSet = new Set(initialProviderMetadata.map(p => p.provider))

            let allMetaDataSuccess = true

            // 1. Update existing providers or create new ones
            for (const provider of validProviders) {
                try {
                    const metaDataPackageReq = {
                        code: modalUpdate.data.dataPack,
                        original_price: provider.price.toString(),
                        source: modalUpdate.data.source,
                        service_code: provider.serviceCode,
                        discount_amount: provider.discountAmount.toString(),
                        net_price: provider.netPrice.toString(),
                        group_code: provider.groupCode,
                        provider: provider.provider
                    }

                    // Check if provider exists in initial metadata
                    const isExistingProvider = initialProviderSet.has(provider.provider)

                    let respMetaData
                    if (isExistingProvider) {
                        // Update existing provider
                        respMetaData = await apiUpdateMetaDataPackageDataSponsor({
                            metadataPackage: metaDataPackageReq,
                            otpConfirm: otpConfirm
                        })
                        console.log('Updated metadata for provider:', provider.provider)
                    } else {
                        // Create new provider
                        respMetaData = await apiAddMetaDataPackageDataSponsor({
                            metadataPackage: metaDataPackageReq,
                            otpConfirm: otpConfirm
                        })
                        console.log('Created metadata for provider:', provider.provider)
                    }

                    if (respMetaData.status !== 200) {
                        allMetaDataSuccess = false
                        console.error('Failed to save metadata for provider:', provider.provider)
                    }
                } catch (error) {
                    allMetaDataSuccess = false
                    console.error('Error saving metadata for provider:', provider.provider, error)
                }
            }

            // 2. Delete providers that were in initial but not in current
            for (const initialProvider of initialProviderMetadata) {
                if (!currentProviderSet.has(initialProvider.provider)) {
                    try {
                        const respDelete = await apiDeleteMetaDataPackageDataSponsor({
                            code: modalUpdate.data.dataPack,
                            source: modalUpdate.data.source,
                            provider: initialProvider.provider,
                            otpConfirm: otpConfirm
                        })

                        if (respDelete.status !== 200) {
                            allMetaDataSuccess = false
                            console.error('Failed to delete metadata for provider:', initialProvider.provider)
                        } else {
                            console.log('Deleted metadata for provider:', initialProvider.provider)
                        }
                    } catch (error) {
                        allMetaDataSuccess = false
                        console.error('Error deleting metadata for provider:', initialProvider.provider, error)
                    }
                }
            }

            if (allMetaDataSuccess) {
                toastSuccess('Cập nhật gói cước thành công!')
                handleCloseModal()
                navigate(navigateBack)
            } else {
                toastError('Cập nhật gói cước thành công nhưng một số metadata thất bại!')
            }
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 200, marginBottom: 20 }}>
                <Button
                    onClick={() => navigate(navigateBack)}
                    icon={<LeftCircleOutlined />}
                    style={{ background: 'gray', color: 'white' }}
                >
                    Trở lại
                </Button>
            </div>
            <Form
                style={{ maxWidth: 900, width: '90%' }}
                form={formUpdate}
                onFinish={handleOpenModal}
                autoComplete='off'
                labelCol={{ span: 4 }}
                labelAlign="left"
            >
                <Form.Item name='ID' style={{ display: 'none' }} />
                <Form.Item
                    label='Tên gói cước'
                    name='name'
                    rules={[{ required: true, message: 'Vui lòng nhập tên gói cước' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label='Mã gói cước'
                    name='dataPack'
                    rules={[{ required: true, message: 'Vui lòng nhập mã gói cước' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                  label='Nguồn'
                  name='source'
                  rules={[{ required: true, message: 'Vui lòng chọn nguồn' }]}
                >
                    <Select
                      placeholder='Chọn nguồn'
                      options={partnerPackageDataSponsorSourceOptions}
                    />
                </Form.Item>
                <Form.Item
                  label='Giá gốc'
                  name='priceUnit'
                  rules={[{ required: true, message: 'Vui lòng nhập giá gốc' }]}
                >
                    <InputNumber
                        formatter={value => formatCurrency(Number(value))}
                        style={{ width: '100%' }}
                        onChange={(value) => {
                            // Recalculate all netPrice values when priceUnit changes
                            const priceValue = Number(value) || 0
                            const newProvider = providerMetadata.map(provider => ({
                                ...provider,
                                netPrice: priceValue - provider.discountAmount
                            }))
                            setProviderMetadata(newProvider)
                        }}
                    />
                </Form.Item>
                <Form.Item
                  label='Phạm vi'
                  name='scope'
                  rules={[{ required: true, message: 'Vui lòng nhập phạm vi' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                  label='Loại'
                  name='type'
                  rules={[{ required: true, message: 'Vui lòng nhập loại' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                  label='Mô tả'
                  name='description'
                  rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                >
                    <Input.TextArea />
                </Form.Item>

                {/* Provider Table */}
                <div style={{ marginBottom: '16px' }}>
                    <Table
                        dataSource={providerMetadata}
                        rowKey={(_, index) => index?.toString() || '0'}
                        pagination={false}
                        bordered
                        columns={[
                            {
                                title: 'STT',
                                key: 'index',
                                render: (_, __, index) => index + 1,
                                width: 60
                            },
                            {
                                title: 'Nhà cung cấp',
                                dataIndex: 'provider',
                                key: 'provider',
                                render: (provider) => mapSourceProviders[provider] || '-'
                            },
                            {
                                title: 'Dịch vụ',
                                dataIndex: 'serviceCode',
                                key: 'serviceCode',
                                render: (serviceCode) => mapServiceCode[serviceCode] || '-'
                            },
                            {
                                title: 'Giá gốc',
                                dataIndex: 'price',
                                key: 'price',
                                render: (price) => formatCurrency(price)
                            },
                            {
                                title: 'Giá chiết khấu',
                                dataIndex: 'discountAmount',
                                key: 'discountAmount',
                                render: (amount) => formatCurrency(amount)
                            },
                            {
                                title: 'Giá bán',
                                dataIndex: 'netPrice',
                                key: 'netPrice',
                                render: (price) => formatCurrency(price)
                            },
                            {
                                title: 'Nhóm',
                                dataIndex: 'groupCode',
                                key: 'groupCode',
                                render: (groupCode) => mapGroupCode[groupCode] || '-'
                            },
                            {
                                title: 'Hành động',
                                key: 'action',
                                align: 'center' as const,
                                render: (_, __, index) => (
                                    <Space>
                                        <Button size="small" onClick={() => handleEditProviderModal(index)}>Sửa</Button>
                                        <Popconfirm
                                            title="Xoá nhà cung cấp"
                                            description="Xoá nhà cung cấp khỏi danh sách?"
                                            onConfirm={() => handleRemoveProvider(index)}
                                            okText="Xác nhận"
                                            cancelText="Huỷ bỏ"
                                        >
                                            <Button size="small" danger>Xóa</Button>
                                        </Popconfirm>
                                    </Space>
                                )
                            }
                        ]}
                        title={() => (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 'bold' }}>Nhà cung cấp</span>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddProviderModal}
                                    disabled={providerMetadata.length >= providerSourceOptions.length}
                                >
                                    Thêm nhà cung cấp
                                </Button>
                            </div>
                        )}
                    />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Space>
                        <Button htmlType="submit" type="primary">Cập nhật</Button>
                        <Button htmlType="reset" type="dashed" danger>Clear</Button>
                    </Space>
                </div>
            </Form>

            {/* Provider Edit Modal */}
            <Modal
                title={modalProviderEdit.index !== null ? 'Sửa nhà cung cấp' : 'Thêm nhà cung cấp'}
                open={modalProviderEdit.isOpen}
                onCancel={() => {
                    setModalProviderEdit({ isOpen: false, index: null })
                    setEditingProvider(null)
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setModalProviderEdit({ isOpen: false, index: null })
                        setEditingProvider(null)
                    }}>
                        Hủy
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleSaveProvider}>
                        {modalProviderEdit.index !== null ? 'Cập nhật' : 'Thêm'}
                    </Button>
                ]}
                width={600}
                centered
            >
                {editingProvider && (
                    <div>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '4px' }}>Nhà cung cấp <span style={{ color: 'red' }}>*</span></div>
                            <Select
                                value={editingProvider.provider}
                                onChange={(value) => handleProviderFieldChange('provider', value)}
                                placeholder='Chọn nhà cung cấp'
                                options={providerSourceOptions}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '4px' }}>Dịch vụ <span style={{ color: 'red' }}>*</span></div>
                            <Select
                                value={editingProvider.serviceCode}
                                onChange={(value) => handleProviderFieldChange('serviceCode', value || '')}
                                placeholder='Chọn dịch vụ'
                                options={serviceCodeOptions}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '4px' }}>Giá gốc <span style={{ color: 'red' }}>*</span></div>
                            <InputNumber
                                formatter={value => formatCurrency(Number(value))}
                                value={editingProvider.price}
                                onChange={(value) => handleProviderFieldChange('price', value || 0)}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '4px' }}>Giá chiết khấu <span style={{ color: 'red' }}>*</span></div>
                            <InputNumber
                                formatter={value => formatCurrency(Number(value))}
                                value={editingProvider.discountAmount}
                                onChange={(value) => handleProviderFieldChange('discountAmount', value || 0)}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '4px' }}>Giá bán <span style={{ color: 'red' }}>*</span></div>
                            <InputNumber
                                formatter={value => formatCurrency(Number(value))}
                                value={editingProvider.netPrice}
                                disabled
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '4px' }}>Nhóm <span style={{ color: 'red' }}>*</span></div>
                            <Select
                              value={editingProvider.groupCode}
                              onChange={(value) => handleProviderFieldChange('groupCode', value || '')}
                              placeholder='Chọn nhóm'
                              options={groupCodeOptions}
                              style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}
            </Modal>

            <Modal title='Cập nhật gói cước' open={modalUpdate.isOpen}
                onCancel={handleCloseModal}
                footer={[
                    <Button onClick={handleConfirm} type='primary'>
                        Xác nhận
                    </Button>,
                ]}>
                <p><span style={{ color: 'red' }}>*</span> Mã OTP xác nhận</p>
                <Input value={otpConfirm} onChange={e => setOtpConfirm(e.target.value)} />
            </Modal>
        </div>
    )
}
